import ArrowLeft from "@/assets/icons/ArrowLeft";
import ArrowLeftIcon from "@/assets/icons/ArrowLeft";
import ArrowRight from "@/assets/icons/ArrowRight";
import Screen from "@/components/layout/Screen";
import { ActiveSessionCard } from "@/components/shared/ActiveSessionCard";
import AppButton from "@/components/shared/AppButton";
import BaseModal from "@/components/shared/BaseModal";
import PageTitle from "@/components/shared/PageTitle/PageTitle";
import PassphraseModal from "@/components/shared/PassphraseModal";
import RateSelection from "@/components/shared/RateSelection";
import { Box } from "@/components/ui/box";
import { Heading } from "@/components/ui/heading";
import { HStack } from "@/components/ui/hstack";
import { Skeleton, SkeletonText } from "@/components/ui/skeleton";
import { VStack } from "@/components/ui/vstack";
import { useRates } from "@/hooks/useRates";
import { useSessionsData } from "@/hooks/useSessions";
import { useToastNotify } from "@/stores/toastStore";
import { saveToStorage } from "@/utils/secureStore";
import * as Sentry from "@sentry/react-native";
import { t } from "@transifex/native";
import { useFocusEffect, useRouter } from "expo-router";
import { useLocalSearchParams } from "expo-router/build/hooks";
import type { RateMini } from "ipctg-inflow-types/InFlowAPIMisc";
import { useCallback, useState } from "react";
import { Platform } from "react-native";
import { v4 as uuidv4 } from "uuid";

const SelectRate = () => {
  const { sessionID = "" } = useLocalSearchParams<{ sessionID?: string }>();
  const { canExtend = "false" } = useLocalSearchParams<{ canExtend?: string }>();
  const { data: sessionData } = useSessionsData();
  const { rates, isFetching, noRatesMessage } = useRates();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [warningMessage, setWarningMessage] = useState<string | null>("");
  const { notify } = useToastNotify();
  const router = useRouter();

  const [selectedRate, setSelectedRate] = useState<RateMini["rateID"]>("");
  const [selectedRateName, setSelectedRateName] = useState<string>("");
  const [isPassphraseModalOpen, setIsPassphraseModalOpen] = useState(false);

  const session = sessionData?.parkingSessions?.find((s) => s.session?.sessionID === sessionID)?.session ?? null;

  const safeRates = rates ?? [];

  useFocusEffect(() => {
    const initializeState = async () => {
      try {
        await saveToStorage("isPurchasingPass", "false");
      } catch (error) {
        console.error("Error initializing state:", error);
        Sentry.captureException(error, {
          extra: {
            localStorage: Platform.OS === "web" ? window.localStorage : null,
          },
        });
      }
    };

    initializeState();
  });

  const handleRateSelected = useCallback(async (rate: RateMini, rateName: string) => {
    // TODO remove async and trycatch. There is nothing that throws here or is async.
    try {
      if (rate.warningMessage) {
        setWarningMessage(rate.warningMessage);
      } else {
        setWarningMessage(null);
      }
      setSelectedRate(rate.rateID);
      setSelectedRateName(rateName);
    } catch (error) {
      console.error("Error saving rate:", error);
      Sentry.captureException(error, {
        extra: {
          localStorage: Platform.OS === "web" ? window.localStorage : null,
        },
      });
    }
  }, []);

  const handleContinue = async () => {
    if (!selectedRate) {
      notify({ severity: "error", message: t("No rate is selected") });
      return;
    }

    const requiresPlate = session && canExtend === "true";
    if (requiresPlate && !session?.plate) {
      notify({ severity: "warning", message: t("Plate is required for this session.") });
      return;
    }

    const selectedRateDetails = rates?.find((rate) => rate.rateID === selectedRate);

    if (selectedRateDetails?.requiresPassphrase && warningMessage) {
      setIsModalOpen(true);
    } else if (selectedRateDetails?.requiresPassphrase) {
      setIsPassphraseModalOpen(true);
    } else if (warningMessage) {
      setIsModalOpen(true);
    } else {
      navigateToCart();
    }
  };

  const navigateToCart = () => {
    setSelectedRate("");
    router.push({
      params: { isPurchasingPass: "false", rateID: selectedRate, rateName: selectedRateName },
      pathname: "/cart",
    });
  };

  const handleGoBack = useCallback(() => {
    setSelectedRate("");
    if (!router.canGoBack()) {
      router.replace("/welcome");
    } else {
      router.back();
    }
  }, [router]);

  if (isFetching) {
    return <SelectRateSkeleton />;
  }

  return (
    <Screen>
      <VStack className="gap-[32px]">
        <PageTitle title={t("Parking Rates")} />

        {session && <ActiveSessionCard session={session} canExtend={false} onExtend={() => {}} />}
        <Box>
          {safeRates.length > 0 ? (
            <RateSelection rates={safeRates} onRateSelected={handleRateSelected} selectedRate={selectedRate} />
          ) : (
            <Box className="text-center">
              <Heading className="text-center text-typography-900 text-lg">{noRatesMessage}</Heading>
            </Box>
          )}
        </Box>

        <HStack space="md">
          <AppButton className="flex-1" variant="outline" onPress={handleGoBack} icon={ArrowLeft} title={t("Back")} />
          {safeRates.length > 0 && (
            <AppButton className="flex-1" onPress={handleContinue} disabled={!selectedRate} iconPosition="after" icon={ArrowRight} title={t("Next")} />
          )}
        </HStack>

        <PassphraseModal isOpen={isPassphraseModalOpen} onClose={() => setIsPassphraseModalOpen(false)} rateID={selectedRate} rateName={selectedRateName} />

        <BaseModal
          title={t("Notice")}
          subtitle={t(warningMessage ?? "")}
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          buttons={[
            { title: "Back", onPress: () => setIsModalOpen(false), variant: "outline", icon: ArrowLeftIcon },
            {
              title: "Proceed",
              onPress: () => {
                setIsModalOpen(false);
                const selectedRateDetails = rates?.find((rate) => rate.rateID === selectedRate);
                if (selectedRateDetails?.requiresPassphrase) {
                  setIsPassphraseModalOpen(true);
                } else {
                  navigateToCart();
                }
              },
            },
          ]}
        />
      </VStack>
    </Screen>
  );
};

const SelectRateSkeleton = () => {
  return (
    <Screen>
      <VStack space="md" className="gap-[32px]">
        <Box className="items-center">
          <SkeletonText className="h-8 w-3/4" />
        </Box>
        <VStack space="sm" className="gap-[12px]">
          {[...Array(5)].map(() => (
            <Skeleton key={uuidv4()} className="h-[64px] w-full rounded-md" />
          ))}
        </VStack>
        <HStack space="md" className="mt-4">
          <Skeleton className="h-12 w-1/2 rounded-md" />
          <Skeleton className="h-12 w-1/2 rounded-md" />
        </HStack>
      </VStack>
    </Screen>
  );
};

export default SelectRate;
